import { $isCodeNode } from "@lexical/code-core";
import { $isListNode } from "@lexical/list";
import { $isHeadingNode, $isQuoteNode } from "@lexical/rich-text";
import { $findMatchingParent } from "@lexical/utils";
import {
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  type LexicalEditor,
  type LexicalNode,
} from "lexical";
import type { AttachmentRef, BlockType } from "../index";
import type { TrayStore } from "./attachment-tray";
import type { PendingStore } from "./composer";
import { $canLink, $readInline, type InlineState } from "./toolbar-store";

/** What the controls around a composer show. Read it in a slot with `useComposerState()`. */
export interface ComposerState extends InlineState {
  /** The block the selection starts in, or `null` without a text selection. */
  block: BlockType | null;
  /** False in a code block, where text is literal and marks do nothing. */
  canFormat: boolean;
  /** Whether there is text selected that a link could go on. */
  canLink: boolean;
  /** What the empty-message policy said of the draft, as of the last time its text changed. */
  empty: boolean;
  /** Whether a send is waiting for the host. */
  pending: boolean;
  /** The files given to the message, in order. Always empty where the composer has no store. */
  attachments: readonly AttachmentRef[];
  /** Whether a file that was given is still being kept by the store; sending waits for it. */
  attachmentsPending: boolean;
  /** False while read-only, and while a shared document is still arriving. */
  editable: boolean;
}

export interface ComposerStore {
  subscribe(listener: () => void): () => void;
  getSnapshot(): ComposerState;
}

export interface ComposerStoreOptions {
  editor: LexicalEditor;
  pending: PendingStore;
  tray: TrayStore | null;
  /** The host's policy, asked with the draft as it is now. */
  isEmpty(): boolean;
}

type SelectionState = Omit<
  ComposerState,
  "empty" | "pending" | "editable" | "attachments" | "attachmentsPending"
>;

const NO_SELECTION: SelectionState = {
  bold: false,
  italic: false,
  strikethrough: false,
  code: false,
  link: null,
  block: null,
  canFormat: true,
  canLink: false,
};

const LIST_BLOCKS = { bullet: "bullet", number: "number", check: "check" } as const;

/** The innermost list wins, so an item of a numbered list inside a bulleted one is numbered. */
function $blockOf(node: LexicalNode): BlockType | null {
  for (let current: LexicalNode | null = node; current !== null; current = current.getParent()) {
    if ($isListNode(current)) return LIST_BLOCKS[current.getListType()];
    if ($isCodeNode(current)) return "code";
    if ($isHeadingNode(current)) return current.getTag();
    if ($isQuoteNode(current)) return "quote";
    if ($isParagraphNode(current)) return "paragraph";
  }
  return null;
}

function $readSelection(): SelectionState {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return NO_SELECTION;
  const anchor = selection.anchor.getNode();
  return {
    ...$readInline(selection),
    block: $blockOf(anchor),
    canFormat: $findMatchingParent(anchor, $isCodeNode) === null,
    canLink: $canLink(selection),
  };
}

const NO_ATTACHMENTS: readonly AttachmentRef[] = [];

const KEYS: readonly (keyof ComposerState)[] = [
  "bold",
  "italic",
  "strikethrough",
  "code",
  "link",
  "block",
  "canFormat",
  "canLink",
  "empty",
  "pending",
  "editable",
  "attachments",
  "attachmentsPending",
];

/**
 * Everything a composer's controls show, in one snapshot that is replaced only when something in
 * it changes. Typing on changes none of it, so typing re-renders no control.
 */
export function createComposerStore(options: ComposerStoreOptions): ComposerStore {
  const { editor, pending, tray } = options;
  // The policy is the host's code and may be slow, so it is asked only when the draft has changed:
  // its text, or what is in its tray, since a message that is only a file is a message.
  let text: string | null = null;
  let trayAt = -1;
  let empty = true;
  const listeners = new Set<() => void>();
  let unregister: (() => void) | null = null;

  const $isEmpty = (): boolean => {
    const now = $getRoot().getTextContent();
    const revision = tray?.getRevision() ?? 0;
    if (now === text && revision === trayAt) return empty;
    text = now;
    trayAt = revision;
    try {
      empty = options.isEmpty();
    } catch {
      // A policy that throws has not said the draft is empty.
      empty = false;
    }
    return empty;
  };

  const read = (): ComposerState => {
    const state = editor.getEditorState();
    return {
      ...state.read($readSelection),
      empty: state.read($isEmpty),
      pending: pending.get(),
      editable: editor.isEditable(),
      attachments: tray?.getState().attachments ?? NO_ATTACHMENTS,
      attachmentsPending: (tray?.getState().staging ?? 0) > 0,
    };
  };

  // Read now, not at the first subscription: a stored draft must not show as empty for a frame.
  let snapshot = read();

  const refresh = (): void => {
    const next = read();
    if (KEYS.every((key) => snapshot[key] === next[key])) return;
    snapshot = next;
    for (const listener of listeners) listener();
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      if (unregister === null) {
        const offUpdate = editor.registerUpdateListener(refresh);
        const offEditable = editor.registerEditableListener(refresh);
        const offPending = pending.subscribe(refresh);
        const offTray = tray?.subscribe(refresh);
        unregister = () => {
          offUpdate();
          offEditable();
          offPending();
          offTray?.();
        };
        refresh();
      }
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          unregister?.();
          unregister = null;
        }
      };
    },
    getSnapshot: () => snapshot,
  };
}
